import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Box, Divider, Link, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import VerificationEmptyState, { VerificationEmptyStateProps } from 'features/services/shared/VerificationEmptyState';
import { Fragment } from 'react';
import { Link as RouterLink } from 'react-router-dom';

export interface DisabledNonFaUwProduct {
  name: string;
  productCode?: string;
  linkTestId: string;
}

interface NonFaUwEmptyStateProps
  extends Pick<
    VerificationEmptyStateProps,
    'icon' | 'headerText' | 'descriptionText' | 'actionLabel' | 'onAction' | 'isActionDisabled' | 'actionTestId'
  > {
  agentNetAdmin?: boolean;
  promptHeading: string;
  disabledProducts: DisabledNonFaUwProduct[];
  promptTestId: string;
  containerTestId?: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      margin: 'auto',
      width: 'fit-content',
      maxWidth: pxToRem(650),
    },
    prompt: {
      width: '100%',
    },
    divider: {
      margin: theme.spacing(4, 0),
    },
    heading: {
      color: theme.palette.text.primary,
      textAlign: 'center',
      fontSize: pxToRem(16),
      fontWeight: 500,
      lineHeight: '150%',
      marginBottom: theme.spacing(1),
    },
    message: {
      color: theme.palette.text.secondary,
      textAlign: 'center',
      fontSize: pxToRem(14),
      fontWeight: 400,
      lineHeight: '150%',
    },
  }),
);

const NonFaUwEmptyState = ({
  icon,
  headerText,
  descriptionText,
  actionLabel,
  onAction,
  isActionDisabled,
  actionTestId,
  agentNetAdmin,
  promptHeading,
  disabledProducts,
  promptTestId,
  containerTestId,
}: NonFaUwEmptyStateProps) => {
  const classes = useStyles();

  return (
    <Box className={classes.container} data-testid={containerTestId}>
      <VerificationEmptyState
        icon={icon}
        headerText={headerText}
        descriptionText={descriptionText}
        isProductEnabled
        actionLabel={actionLabel}
        onAction={onAction}
        isActionDisabled={isActionDisabled}
        actionTestId={actionTestId}
      />
      {disabledProducts.length > 0 && (
        <Box className={classes.prompt} data-testid={promptTestId}>
          <Divider className={classes.divider} />
          <Typography className={classes.heading}>{promptHeading}</Typography>
          <Typography className={classes.message}>
            {agentNetAdmin ? (
              <>
                Enable{' '}
                {disabledProducts.map((product, index) => (
                  <Fragment key={product.name}>
                    {index > 0 && ' and '}
                    <Link
                      component={RouterLink}
                      to={`/admin/integrations-features?productCode=${product.productCode}`}
                      underline="always"
                      color="primary"
                      data-testid={product.linkTestId}
                    >
                      {product.name}
                    </Link>
                  </Fragment>
                ))}{' '}
                for other underwriters.
              </>
            ) : (
              `Ask your admin to enable ${disabledProducts
                .map((product) => product.name)
                .join(' and ')} for other underwriters.`
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default NonFaUwEmptyState;
